/**
 * Live-render reconciliation (KHIYARSA-EDITOR-1 Phase 3).
 *
 * The editor re-renders on every keystroke. Doing that by deleting the old
 * elements and inserting new ones would drop the selection, rebuild every
 * binding and churn the scene on each character. Instead we reconcile: the
 * compiler is deterministic (`emit.ts` derives every id from the source, and
 * `convertToExcalidrawElements` is called with `regenerateIds: false`), so the
 * same KMD node keeps the same key across compiles and its canvas element can
 * be updated in place.
 *
 * The one identity that is NOT stable is a bound label: `bindTextToContainer`
 * (packages/element/src/transform.ts) creates it with `newTextElement` and no
 * id, so every compile mints a fresh one. Labels are therefore matched through
 * their container's `boundElements`, never by id — the caveat the EDITOR-1
 * contract calls out.
 *
 * This module is deliberately free of React and of `@excalidraw/excalidraw`
 * app internals so it can be unit-tested directly.
 */
import type { ExcalidrawElement } from "@excalidraw/element/types";

/**
 * Marker for "this element is currently driven by the editor".
 *
 * Deliberately a single flag rather than a per-session id. An earlier version
 * tagged elements with a per-hook-instance session id, and a remount — React
 * StrictMode in dev, or simply reopening the panel — produced a SECOND session
 * that rendered its own copy alongside the first: duplicate element ids, an
 * invalid scene, and elements that could not be dragged. There is only ever
 * one editor panel, so one flag is the honest model.
 *
 * The flag is stripped at the commit boundary: a committed diagram is made of
 * ordinary elements, and only the in-flight one is live. That is what keeps a
 * later session from adopting or replacing work the user already committed.
 */
export const LIVE_KEY = "kmdLive";

type Mutable = ExcalidrawElement & {
  customData?: Record<string, any>;
  boundElements?: { id: string; type: string }[] | null;
  containerId?: string | null;
  frameId?: string | null;
  startBinding?: { elementId: string } | null;
  endBinding?: { elementId: string } | null;
};

const khiyarsaOf = (el: ExcalidrawElement): { id?: string; role?: string } =>
  ((el as Mutable).customData?.khiyarsa as { id?: string; role?: string }) ??
  {};

/** stable identity of a compiler-emitted element within one document */
export const keyOf = (el: ExcalidrawElement): string | null => {
  const { id, role } = khiyarsaOf(el);
  return id && role ? `${role}:${id}` : null;
};

/** true when the editor is currently driving this element */
export const isLive = (el: ExcalidrawElement): boolean =>
  (el as Mutable).customData?.[LIVE_KEY] === true;

/**
 * Mark elements as editor-driven, so the next reconcile — and the detach
 * detector — can tell them apart from the user's own drawing.
 */
export const stampLive = <T extends ExcalidrawElement>(
  elements: readonly T[],
): T[] =>
  elements.map(
    (el) =>
      ({
        ...el,
        customData: {
          ...((el as Mutable).customData ?? {}),
          [LIVE_KEY]: true,
        },
      } as T),
  );

/**
 * Drop the live marker — the diagram becomes ordinary elements. Called at the
 * commit boundary and on detach, so nothing later reclaims them.
 */
export const stripLive = <T extends ExcalidrawElement>(
  elements: readonly T[],
): T[] =>
  elements.map((el) => {
    if (!isLive(el)) {
      return el;
    }
    const { [LIVE_KEY]: _live, ...rest } = (el as Mutable).customData ?? {};
    return {
      ...el,
      customData: Object.keys(rest).length > 0 ? rest : undefined,
    } as T;
  });

/** the single bound text child of a container, if it has one */
const boundTextIdOf = (
  el: ExcalidrawElement,
  byId: Map<string, ExcalidrawElement>,
): string | null => {
  const bound = (el as Mutable).boundElements;
  if (!bound) {
    return null;
  }
  for (const ref of bound) {
    if (ref.type === "text" && byId.has(ref.id)) {
      return ref.id;
    }
  }
  return null;
};

/**
 * Map ids in the freshly compiled scene onto the ids already on canvas, so a
 * re-render mutates elements rather than replacing them.
 *
 * Returns `next` rewritten: every element that matches something in `previous`
 * adopts its id, and every reference (bindings, boundElements, containerId,
 * frameId, frame children) is rewritten to match.
 */
export const reconcileElements = (
  previous: readonly ExcalidrawElement[],
  next: readonly ExcalidrawElement[],
): ExcalidrawElement[] => {
  const prevById = new Map(previous.map((el) => [el.id, el]));
  const nextById = new Map(next.map((el) => [el.id, el]));

  const prevByKey = new Map<string, ExcalidrawElement>();
  for (const el of previous) {
    const key = keyOf(el);
    if (key && !prevByKey.has(key)) {
      prevByKey.set(key, el);
    }
  }

  /** new id → id to keep */
  const idMap = new Map<string, string>();

  for (const el of next) {
    const key = keyOf(el);
    if (!key) {
      continue;
    }
    const match = prevByKey.get(key);
    // only reuse an id when the element type is unchanged; a `{decision}` that
    // becomes a `{process}` is a different shape and must be a new element
    if (match && match.type === el.type && !idMap.has(el.id)) {
      idMap.set(el.id, match.id);
      // labels carry no customData of their own — pair them through their
      // containers, which is the only stable route
      const nextLabel = boundTextIdOf(el, nextById);
      const prevLabel = boundTextIdOf(match, prevById);
      if (nextLabel && prevLabel && !idMap.has(nextLabel)) {
        idMap.set(nextLabel, prevLabel);
      }
    }
  }

  if (idMap.size === 0) {
    return [...next];
  }

  const remap = (id: string): string => idMap.get(id) ?? id;

  return next.map((el) => {
    const source = el as Mutable;
    const out: Mutable = { ...source, id: remap(source.id) };

    if (source.boundElements) {
      out.boundElements = source.boundElements.map((ref) => ({
        ...ref,
        id: remap(ref.id),
      }));
    }
    if (source.containerId) {
      out.containerId = remap(source.containerId);
    }
    if (source.frameId) {
      out.frameId = remap(source.frameId);
    }
    if (source.startBinding) {
      out.startBinding = {
        ...source.startBinding,
        elementId: remap(source.startBinding.elementId),
      };
    }
    if (source.endBinding) {
      out.endBinding = {
        ...source.endBinding,
        elementId: remap(source.endBinding.elementId),
      };
    }
    return out as ExcalidrawElement;
  });
};

/**
 * Build the full element array for `updateScene`: everything the editor does
 * not drive, in its original order, followed by the reconciled render.
 *
 * Besides the currently-live elements, this ADOPTS any element already on
 * canvas whose compiler key the new render also produces. Without that, a
 * committed diagram plus a restored draft render as two overlapping copies of
 * the same picture — reload the page with a draft saved and the editor would
 * lay its render straight on top of the diagram it committed before the
 * reload. Matching on the compiler key means we only ever adopt elements this
 * document demonstrably generated, and the effect is what a user expects:
 * reopening the editor keeps editing the diagram the text describes.
 */
export const mergeIntoScene = (
  sceneElements: readonly ExcalidrawElement[],
  rendered: readonly ExcalidrawElement[],
): ExcalidrawElement[] => {
  const renderedKeys = new Set(
    rendered.map(keyOf).filter((k): k is string => k !== null),
  );
  const adoptable = (el: ExcalidrawElement) => {
    const key = keyOf(el);
    return key !== null && renderedKeys.has(key);
  };
  // a bound label carries no key of its own; it comes along with its container
  const adoptedIds = new Set<string>();
  for (const el of sceneElements) {
    if (isLive(el) || adoptable(el)) {
      adoptedIds.add(el.id);
      for (const ref of (el as Mutable).boundElements ?? []) {
        adoptedIds.add(ref.id);
      }
    }
  }

  const previous = sceneElements.filter((el) => adoptedIds.has(el.id));
  const untouched = sceneElements.filter((el) => !adoptedIds.has(el.id));
  return [...untouched, ...stampLive(reconcileElements(previous, rendered))];
};

/**
 * Fingerprint of the things a USER can change by hand.
 *
 * `element.version` is the obvious signal and the wrong one: Excalidraw bumps
 * it during its own post-render settling — arrow binding, bound-text layout,
 * frame membership — so a version comparison detaches the moment the diagram
 * first renders, before the user has touched anything.
 *
 * So compare what a hand edit actually moves:
 *  - arrows are skipped entirely; their points and bindings are recomputed by
 *    the engine constantly and are not something the user edits directly here
 *  - text is compared on its content only, never its geometry, which
 *    `redrawTextBoundingBox` owns
 *  - everything else is compared on position, size, angle and colour
 */
const fingerprintOf = (el: ExcalidrawElement): string | null => {
  const any = el as Mutable & {
    text?: string;
    angle?: number;
    strokeColor?: string;
    backgroundColor?: string;
  };
  if (el.type === "arrow" || el.type === "line") {
    return null;
  }
  if (el.type === "text") {
    return `t:${any.text ?? ""}`;
  }
  return [
    Math.round(el.x),
    Math.round(el.y),
    Math.round(el.width),
    Math.round(el.height),
    any.angle ?? 0,
    any.strokeColor ?? "",
    any.backgroundColor ?? "",
    el.isDeleted ? 1 : 0,
  ].join("|");
};

export const snapshotFingerprints = (
  elements: readonly ExcalidrawElement[],
): Map<string, string> => {
  const out = new Map<string, string>();
  for (const el of elements) {
    if (!isLive(el)) {
      continue;
    }
    const print = fingerprintOf(el);
    if (print !== null) {
      out.set(el.id, print);
    }
  }
  return out;
};

/**
 * Has the user hand-edited anything the editor rendered? That is what detaches
 * the diagram from the text — KMD has no coordinate syntax (spec §4), so a
 * moved node cannot be written back (see the contract, §B2).
 */
export const detectForeignEdit = (
  sceneElements: readonly ExcalidrawElement[],
  written: ReadonlyMap<string, string>,
): boolean => {
  if (written.size === 0) {
    return false;
  }
  let seen = 0;
  for (const el of sceneElements) {
    if (!isLive(el)) {
      continue;
    }
    const print = fingerprintOf(el);
    if (print === null) {
      continue;
    }
    const before = written.get(el.id);
    if (before === undefined) {
      // a live element we never wrote — e.g. the user duplicated one
      return true;
    }
    seen++;
    if (before !== print) {
      return true;
    }
  }
  // something we rendered was deleted outright
  return seen < written.size;
};
